<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card class="mx-auto my-auto">
          <v-toolbar
            color="indigo"
            dark>

            <v-toolbar-title>
              <v-icon left>{{ mdiCarSide }}</v-icon>
              {{ $vuetify.lang.t('$vuetify.map.managerMap.toolbarTitle') }}
            </v-toolbar-title>

            <map-manager-local-filters @map-table-refresh="options.page = 1; getUsers()"
                                       v-if="isSpecial"></map-manager-local-filters>

            <v-spacer></v-spacer>

            <tooltip-basic v-if="isSpecial"
                           :icon="clientFilesIcon"
                           text="$vuetify.map.requests.files"
                           emit-state="open_requests_datatable"
                           @open_requests_datatable="requestsDatatableDialog = true"
            ></tooltip-basic>

            <v-tooltip top v-if="isSpecial">
              <template v-slot:activator="{ on }">
                <v-btn icon class="text-right ml-2" v-on="on" @click="demoVideoDialog = true">
                  <v-icon>{{ demoVideoIcon }}</v-icon>
                </v-btn>
              </template>
              <span>{{ $vuetify.lang.t('$vuetify.map.managerMap.demoVideo') }}</span>
            </v-tooltip>

            <map-settings-menu key="generalSettings" v-if="isSpecial"></map-settings-menu>

            <v-tooltip top v-if="isAdmin">
              <template v-slot:activator="{ on }">
                <v-btn icon class="text-right ml-2" v-on="on" @click="openDefaultSettings">
                  <v-icon>{{ defaultSettingsIcon }}</v-icon>
                </v-btn>
              </template>
              <span>{{ $vuetify.lang.t('$vuetify.map.managerMap.defaultSettings') }}</span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn icon @click="clearAll" v-on="on">
                  <v-icon>{{ filterRemoveIcon }}</v-icon>
                </v-btn>
              </template>
              <span>{{ $vuetify.lang.t('$vuetify.map.tableFilter.clearAll') }}</span>
            </v-tooltip>

          </v-toolbar>

          <div class="manager-datatable" v-if="isLoaded">
            <v-data-table :headers="headers" :items="users" :options.sync="options" :loading="tableLoading"
                          :server-items-length="totalUsers" multi-sort :expanded.sync="expanded" show-expand
                          item-key="email" :mobile-breakpoint="0" @click:row="expandRow" @item-expanded="itemExpanded"
                          :hide-default-footer="!isSpecial" style="height:auto;"
                          :footer-props="footerProps" class="elevation-1">

              <!-- header slots -->
              <template v-for="item in iconedHeaders" v-slot:[`header.${item}`]="{header}">
              <span :key="item" class="d-inline-flex align-center justify-start"
                    style="vertical-align: middle; word-wrap: break-word">
                <v-tooltip top v-if="header.hasOwnProperty('icon')">
                  <template v-slot:activator="{ on }">
                    <v-icon left class="pointer" v-on="on">{{ header.icon }}</v-icon>
                  </template>
                  <span>{{ header.text }}</span>
                </v-tooltip>
              </span>
              </template>

              <!-- filter -->
              <template v-slot:body.prepend="{ headers }">
                <tr>
                  <td v-for="header in headers" :key="header.value" :colspan="1">
                    <template v-if="filters.hasOwnProperty(header.value) && !specialOnly(header.value)">
                      <text-filter v-if="filters[header.value].type === 'text'" :column="header.value"
                                   :header="filters[header.value].header"
                                   @value-changed="filterColumn"></text-filter>

                      <range-filter
                        v-if="filters[header.value].type === 'range'"
                        :column="header.value" :disabled="filters[header.value].maxServerValue === 0"
                        :header="header.text" :max="filters[header.value].maxServerValue"
                        @value-changed="filterColumn"></range-filter>

                      <datetime-filter ref="travel_datetime_filter" id="datetime-filter"
                                       v-if="filters[header.value].type === 'datetime'"
                                       :column="header.value"
                                       :max="dateToCalendarEntry(new Date())"
                                       :header="filters[header.value].hasOwnProperty('header') ? filters[header.value].header : header.text"
                                       @datetime-changed="datetimeUpdate"
                                       @datetime-cleared="datetimeCleared"></datetime-filter>
                    </template>
                  </td>
                </tr>
              </template>

              <template v-slot:item.style="{item}">
                <div class="d-inline-flex">
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-sheet class="style-cursor" :color="item.style" tile width="20" height="20" elevation="3"
                               v-on="on"></v-sheet>
                    </template>
                    <span>{{ $vuetify.lang.t(`$vuetify.map.managerMap.style.${item.style}`) }}</span>
                  </v-tooltip>

                </div>
              </template>

              <template v-slot:item.states="{item}">
                <v-tooltip top class="align-center" v-if="item.states > 0">
                  <template v-slot:activator="{ on }">
                    <v-icon left v-on="on">
                      {{ stateUncheckedIcon }}
                    </v-icon>
                  </template>
                  <span>{{ $vuetify.lang.t(`$vuetify.map.managerMap.uncheckedStates`, item.states) }}</span>
                </v-tooltip>
              </template>

              <template v-slot:item.username="{item}">
                <v-chip pill @click.stop="loadUserProfile({userId: item.id, username: item.username})">
                  <v-avatar left v-if="item.avatar">
                    <v-img :src="item.avatar"></v-img>
                  </v-avatar>
                  {{ item.username }}
                </v-chip>
              </template>
              <template v-slot:item.distance="{item}">
                {{ valueFormatted(item.distance) }}
              </template>
              <template v-slot:item.latest_trip="{item}">
                {{ travelDateLocalized(item.latest_trip) }}
              </template>
              <template v-slot:expanded-item="{ item, headers }">
                <td :colspan="headers.length" class="cell-expanded">
                  <v-container fluid>

                    <v-row align="center" justify="center" align-content="center">
                      <v-col cols="12">
                        <driver-trips :ref="`driver-${item.id}`" :driver-id="item.id" :outer-filters="filters"
                                      @trip-request="loadTrip"></driver-trips>
                      </v-col>
                    </v-row>
                  </v-container>
                </td>
              </template>
            </v-data-table>
          </div>

        </v-card>
      </v-col>
    </v-row>

    <map-dialog ref="mapDialog" :dialog="tripDialog" :travel-data="travelData" :nano-user="profileData.profile.nanoUser"
                @feedback-passed="updateTravel" @trip-request="loadTrip" @change-trip-request="navigateTrip"
                :next-trip="nextTripNavigation" :previous-trip="previousTripNavigation" :loaded="travelData.isLoaded"
                @close-map-dialog="tripDialog = false" @critical-event-changed="changeCriticalEvent"
                @update-report-state="updateReportTripState"
                @set-all-camera-correct-events="setAllCameraCorrectEvents"
                @set-all-mask-wearing-events="setAllWearingMaskEvents">
    </map-dialog>

    <driver-settings-dialog :dialog="settingsDialog" :profile="profileData.profile" :user-id="profileData.user_id"
                            :loaded="profileData.isLoaded"
                            :settings="profileData.settings" :username="profileData.username"
                            :is-default-settings="profileData.isDefaultSettings"
                            @close-settings-dialog="settingsDialog = false"></driver-settings-dialog>

    <demo-videos-dialog :dialog="demoVideoDialog"
                        @close-demo-video-dialog="demoVideoDialog = false"></demo-videos-dialog>

    <requests-datable :dialog="requestsDatatableDialog"
                      @close-requests-datatable="requestsDatatableDialog = false">
    </requests-datable>

  </v-container>
</template>

<script>
import Vuetify from '../../plugins/vuetify'

import {
  mdiAccount,
  mdiBusAlert, mdiCarSide, mdiClose, mdiCogs,
  mdiEmoticonSadOutline, mdiFileMultiple, mdiFilterRemove, mdiMapClock, mdiMapMarkerDistance,
  mdiRoad, mdiSelection, mdiVideo, mdiVideoCheck,
  mdiWalletTravel
} from '@mdi/js'
import backend from '../../backend'
import { DriverTableEntry } from '../../models/driverTable/driverTableEntry'

import { AUTH_MODULE, DRIVER_TABLE_MODULE, MANAGER_MAP_MODULE, SETTINGS_MODULE } from '../../store/modules'
import { createHelpers } from 'vuex-map-fields'

import { distanceLocalized, travelDateLocalized, valueFormatted } from '../../utils/travels'

import { Travel } from '../../models/travel/Travel'
import { UserFeedback as UserFeedbackModel } from '../../models/travel/UserFeedback'

import DriverTrips from '@/components/map/selectionComponents/driverTrips'
import MapDialog from '@/components/map/mapDialog'
import { getDefaultDriverTable } from '@/store/modules/driverTable'
import TextFilter from '@/components/map/tableFilters/textFilter'
import RangeFilter from '@/components/map/tableFilters/rangeFilter'
import DatetimeFilter from '@/components/map/tableFilters/datetimeFilter'
import { defaultDatetimeFilterState, defaultRangeFilterState } from '@/utils/filters'
import { dateToCalendarEntry } from '@/utils/calendar'
import { UserProfile } from '@/models/user/UserProfile'
import { UserSettings } from '@/models/user/UserSettings'
import DriverSettingsDialog from '@/components/map/driverSettingsDialog'
import snackbarMixin from '@/mixins/snackbarMixin'
import MapSettingsMenu from '@/components/map/settings/settingsMenu'
import DemoVideosDialog from '@/components/map/videoSelector/VideoSelectorDialog'
import { DangerousState, UserCriticalEventConfirmation } from '@/enum'
import MapManagerLocalFilters from '@/components/map/mapManager/mapManagerLocalFilters'
import { defineTableHeader } from '@/utils/tableManager'
import { dangerousStateIcons } from '@/utils/icons'
import TooltipBasic from '@/components/utils/tooltipBasic'
import RequestsDatable from '@/components/map/serverRequests/requestsDatable'
import { getCurrentTimezoneForBackend, labeledTimezones } from '@/utils/timezone'

const { mapFields } = createHelpers({
  getterType: `${MANAGER_MAP_MODULE}/getField`,
  mutationType: `${MANAGER_MAP_MODULE}/updateField`
})

const { mapFields: driverTableFields } = createHelpers({
  getterType: `${DRIVER_TABLE_MODULE}/getField`,
  mutationType: `${DRIVER_TABLE_MODULE}/updateField`
})

const { mapFields: authFields } = createHelpers({
  getterType: `${AUTH_MODULE}/getField`
})

const { mapFields: settingsFields } = createHelpers({
  getterType: `${SETTINGS_MODULE}/getField`
})

const isCorrectQueryParams = (params) => params.hasOwnProperty('travel') || (params.hasOwnProperty('travel') && params.hasOwnProperty('critical'))

const defaultFilterState = () => ({
  username: {
    type: 'text',
    value: '',
    specialOnly: true,
    header: '$vuetify.map.managerMap.filterHeaders.driverHeader'
  },
  travels: defaultRangeFilterState(),
  // duration: {
  //   type: 'range',
  //   value: [0, 0], // default values - all data will be visible, will be updated later after server call
  //   maxServerValue: 0 // max count - will be fetched from server
  // },
  'latest_trip': {
    type: 'datetime',
    value: {
      start: defaultDatetimeFilterState(),
      finish: defaultDatetimeFilterState()
    },
    header: Vuetify.framework.lang.t('$vuetify.map.managerMap.filterHeaders.latestTrip')
    // maxServerValue: 0
  },
  distance: {
    type: 'range',
    value: [0, 0], // default values - all data will be visible, will be updated later after server call
    maxServerValue: 0, // max count - will be fetched from server
    multiplier: 1000
  },
  // 'distraction_count': defaultRangeFilterState(),
  'distraction_no_attention': defaultRangeFilterState(),
  'distraction_no_face': defaultRangeFilterState(),
  'drowsiness_count': defaultRangeFilterState(),
  'nobelt_count': defaultRangeFilterState(),
  'camera_sabotage': defaultRangeFilterState(),
  'cellphone_use': defaultRangeFilterState(),
  'eating_count': defaultRangeFilterState(),
  'smoking_count': defaultRangeFilterState()
})

const getHeaders = (isManagerRestricted) => {
  const icons = dangerousStateIcons()
  const result = [
    defineTableHeader('$vuetify.map.managerMap.headers.style', 'style', mdiBusAlert, false),
    defineTableHeader('', 'states', null, false),
    defineTableHeader('$vuetify.map.managerMap.headers.username', 'username', mdiAccount),
    defineTableHeader('$vuetify.map.managerMap.headers.travels', 'travels', mdiWalletTravel),
    defineTableHeader('$vuetify.map.managerMap.headers.distance', 'distance', mdiMapMarkerDistance),
    defineTableHeader('$vuetify.map.managerMap.headers.latestTrip', 'latest_trip', mdiMapClock)
  ]


  if (!isManagerRestricted) {
    result.push(...[
      defineTableHeader('$vuetify.map.managerMap.headers.distraction_no_attention', 'distraction_no_attention', icons[DangerousState.DISTRACTION_NO_ATTENTION]),
      defineTableHeader('$vuetify.map.managerMap.headers.distraction_no_face', 'distraction_no_face', icons[DangerousState.DISTRACTION_NO_FACE]),
      defineTableHeader('$vuetify.map.managerMap.headers.drowsiness', 'drowsiness_count', icons[DangerousState.DROWSINESS]),
      defineTableHeader('$vuetify.map.managerMap.headers.nobelt', 'nobelt_count', icons[DangerousState.BELT_NOT_FASTEN]),
      defineTableHeader('$vuetify.map.managerMap.headers.cameraSabotage', 'camera_sabotage', icons[DangerousState.CAMERA_SABOTAGE]),
      defineTableHeader('$vuetify.map.managerMap.headers.cellphoneUse', 'cellphone_use', icons[DangerousState.CELLPHONE_USE]),
      defineTableHeader('$vuetify.map.managerMap.headers.eatingCount', 'eating_count', icons[DangerousState.EATING]),
      defineTableHeader('$vuetify.map.managerMap.headers.smokingCount', 'smoking_count', icons[DangerousState.SMOKING])
    ])
  }

  result.push(defineTableHeader('', 'data-table-expand', null, false, ''))

  return result
}

const defaultDriverProfile = (username = '', userId = -1) => ({
  isLoaded: null,
  username: username,
  user_id: userId,
  isDefaultSettings: false,
  profile: new UserProfile(),
  settings: new UserSettings()
})

const defaultTravelData = () => ({
  isLoaded: null,
  travel: new Travel()
})

export default {
  name: 'ManagerMap',
  components: {
    RequestsDatable,
    TooltipBasic,
    MapManagerLocalFilters,
    DemoVideosDialog,
    MapSettingsMenu,
    DriverSettingsDialog,
    DatetimeFilter,
    RangeFilter,
    TextFilter,
    MapDialog,
    DriverTrips
  },
  mixins: [snackbarMixin],
  computed: {
    ...mapFields(['userData']),
    ...driverTableFields(['drivers']),
    ...authFields(['isSpecial', 'isAdmin', 'isManagerRestricted']),
    ...settingsFields(['mapSettings.hideSmallTrips', 'mapSettings.hideViewedTrips',
      'mapSettings.autoScrollManipulatedEvents', 'mapLocalFilters.isOnlyNano', 'mapLocalFilters.timeRangeFilter']),
    headers () {
      return getHeaders(this.isManagerRestricted)
    },
    iconedHeaders () {
      return this.headers.filter(header => header.hasOwnProperty('icon')).map(header => header.value)
    },
    nextTripNavigation () {
      if (!this.travelData.travel || this.travelData.travel.user.userId === 0 || !this.travelData.isLoaded) {
        return false
      } else {
        const driverId = this.travelData.travel.user.userId

        if (driverId in this.drivers) {
          const eventIndex = this.drivers[driverId].trips.map(trip => trip.id).indexOf(this.travelData.travel.travelId)
          return eventIndex + 1 < this.drivers[driverId].trips.length || this.drivers[driverId].options.page < Math.ceil(this.drivers[driverId].totalTrips / this.drivers[driverId].options.itemsPerPage)
        } else {
          return false
        }
      }
    },
    previousTripNavigation () {
      if (!this.travelData.travel || this.travelData.travel.user.userId === 0 || !this.travelData.isLoaded) {
        return false
      } else {
        const driverId = this.travelData.travel.user.userId
        if (driverId in this.drivers) {
          const eventIndex = this.drivers[driverId].trips.map(trip => trip.id).indexOf(this.travelData.travel.travelId)
          return eventIndex - 1 > -1 || this.drivers[driverId].options.page > 1
        } else {
          return false
        }
      }
    }
  },
  data: () => ({

    // dialogs from different users/maps can be initiated only as one instance, but with different data
    tripDialog: false,
    settingsDialog: false,
    demoVideoDialog: false,
    requestsDatatableDialog: false,

    expanded: [],
    options: {
      sortBy: ['latest_trip'],
      sortDesc: [true],
      itemsPerPage: 10
    },
    // table filters
    filters: defaultFilterState(),
    totalUsers: 0,
    users: [],
    tableLoading: true,
    footerProps: { 'items-per-page-options': [5, 10, 25, 50, -1] }, // if add more - page overflow can be reached!
    calendar: null,
    travelData: defaultTravelData(),
    profileData: defaultDriverProfile(),

    isLoaded: false,

    mdiEmoticonSad: mdiEmoticonSadOutline,
    mdiCarSide: mdiCarSide,
    mdiRoad: mdiRoad,
    mdiSelection: mdiSelection,
    closeIcon: mdiClose,
    filterRemoveIcon: mdiFilterRemove,
    defaultSettingsIcon: mdiCogs,
    demoVideoIcon: mdiVideo,
    stateUncheckedIcon: mdiVideoCheck,
    clientFilesIcon: mdiFileMultiple
  }),
  methods: {
    async expandRow (row) {
      const searchRow = this.expanded.find(item => item.id === row.id)
      if (searchRow) {
        this.expanded = this.expanded.filter(item => item.id !== row.id)
      } else {
        this.expanded.push(row)
        await this.$nextTick()

        if (this.$refs[`driver-${row.id}`]) {
          this.$refs[`driver-${row.id}`].loadTrips()
        }
      }
    },
    async itemExpanded (data) {
      const isClosed = data.value
      const rowItem = data.item

      if (!isClosed) {
        this.expanded = this.expanded.filter(item => item.id !== rowItem.id)
      } else {
        this.expanded.push(rowItem)
        await this.$nextTick()

        if (this.$refs[`driver-${rowItem.id}`]) {
          this.$refs[`driver-${rowItem.id}`].loadTrips()
        }
      }
    },
    async getUsers (data = { includedDriver: null, travelId: null }) {
      this.tableLoading = true

      try {
        const conditions = {}

        // set conditions
        if (this.hideSmallTrips) {
          conditions.hideShortTrips = true
        }

        if (this.hideViewedTrips) {
          conditions.hideViewedTrips = true
        }

        if (this.isOnlyNano) {
          conditions.isOnlyNano = true
        }

        if (this.timeRangeFilter.isUsed) {
          conditions.timeRangeFilter = {
            ...this.timeRangeFilter.filter,
            timezone: getCurrentTimezoneForBackend()
          }
        }

        const result = await backend.fetchMapDrivers({
          ...this.options,
          filters: this.filters,
          conditions,
          includedDriver: data.includedDriver
        })

        if ('data' in result) {
          if (process.env.NODE_ENV !== 'production') {
            console.log(result.data)
          }
          this.users = result.data.items
          this.totalUsers = result.data.total

          // apply filters max value
          if (result.data.hasOwnProperty('max_values')) {
            for (const [column, maxValue] of Object.entries(result.data.max_values)) {
              if (this.filters.hasOwnProperty(column)) {
                this.filters[column].maxServerValue = maxValue
              }
            }
          }

          // create new data in
          for (const user of this.users) {
            if (!(user.id in this.userData)) {
              // we need to add reactive data manually, because on mounted stage `userData` is empty object
              this.$set(this.userData, user.id, new DriverTableEntry())
            }

            if (!(user.id in this.drivers)) {
              // load default table data to driver
              this.$set(this.drivers, user.id, getDefaultDriverTable())
            }

            // manually expand  row with included driver
            if (user.id === data.includedDriver) {
              this.expanded.push(user)
            }
          }

          // open tab automatically, if server send only one row with data
          if (!this.isSpecial && this.expanded.length === 0) {
            this.expanded.push(this.users[0])
          }

          await this.$nextTick()

          for (const entry of this.expanded) {
            if (this.$refs[`driver-${entry.id}`]) {
              // data.travelId here
              const includedTrip = entry.id === data.includedDriver ? data.travelId : null
              this.$refs[`driver-${entry.id}`].loadTrips({ includedTrip }) // TODO: figure it out if we need to update data
            }
          }

          // set page if we have includedDriver field
          if (data.includedDriver && result.data.hasOwnProperty('page')) {
            this.options.page = result.data.page
          }
        }
      } catch (e) {
        this.processFailedRequest(e)
      } finally {
        this.tableLoading = false
      }
    },
    travelColor (item) {
      const distance = item.distance
      const events = item.drowsiness + item.distraction
      let result = 'green'

      if (distance > 0) {
        const ratio = events / distance
        if (ratio >= 0 && ratio <= 1) {
          result = 'green'
        } else if (ratio > 1 && ratio <= 3) {
          result = 'yellow'
        } else {
          result = 'red'
        }
      }

      return result
    },
    async loadTrip (data = { userId: -1, travelStart: -1, travel: null, critical: null, once: false }) {
      // once - page first loading flag for critical popup
      this.travelData.isLoaded = false

      if (!this.tripDialog) {
        this.tripDialog = true
      }

      // clean map dialog local filters
      this.$refs['mapDialog'].clearLocalCriticalFilter()

      try {
        let isProfileLoaded = false
        if (data.hasOwnProperty('userId') && data.userId !== -1) {
          try {
            await this.fetchProfile(data.userId)
            isProfileLoaded = true
          } catch (e) {
            this.profileData = defaultDriverProfile()
          }
        }

        const request = data.travel ? { 'travel_id': data.travel } : {
          user: data.userId,
          timestamp: data.travelStart instanceof Date ? data.travelStart.getTime() : data.travelStart
        }

        const response = await backend.fetchMapTrip(request)
        this.travelData.travel = new Travel(response.data)

        if (!isProfileLoaded) {
          try {
            await this.fetchProfile(this.travelData.travel.user.userId)
          } catch (e) {
            console.error(e)
            this.profileData = defaultDriverProfile()
          }
        }

        if (isCorrectQueryParams(this.$route.query) && data.critical) {
          this.travelData.travel.setActiveCriticalEvent(Number(data.critical))
        }
      } catch (e) {
        console.error(e)
        this.tripDialog = false
        this.processFailedRequest(e)
      } finally {
        this.travelData.isLoaded = true
      }
    },
    updateTravel (data) {
      this.travelData.travel.feedback = data.map(entry => new UserFeedbackModel(entry))
    },
    changeCriticalEvent (data) {
      const criticalEventId = data.request.id_critical_event
      const event = this.travelData.travel.criticalEvents.find(event => event.idCriticalEvent === criticalEventId)

      if (event) {
        event.state = data.request.state
        // demo is suited by switch itself
        event.retrainState = data.request.retrain_state
        event.rejectReason = data.request.reject_reason
        event.rejectComment = data.request.reject_comment

        this.$nextTick(() => {
          if (
            this.autoScrollManipulatedEvents && !data.request.demo_changed &&
            (data.request.state === UserCriticalEventConfirmation.APPROVE ||
              data.request.state === UserCriticalEventConfirmation.REJECT ||
              data.request.state === UserCriticalEventConfirmation.RETRAIN)
          ) {
            this.$refs['mapDialog'].switchCriticalEvent({ direction: 'next', innerIndex: data.index })
          }
        })
      }
    },
    async navigateTrip (direction) {
      // search trip in drive table
      const driverId = this.travelData.travel.user.userId

      const delta = direction === 'next' ? 1 : -1
      const eventIndex = this.drivers[driverId].trips.map(trip => trip.id).indexOf(this.travelData.travel.travelId)

      if (eventIndex > -1) {
        let newIndex = eventIndex + delta

        // hide arrows
        this.travelData.isLoaded = false

        // load additional trip batch if we needed
        if (newIndex < 0 && this.drivers[driverId].options.page > 1) {
          // download previous trips batch
          await this.$refs[`driver-${driverId}`].loadTrips({ page: this.drivers[driverId].options.page - 1 })
          newIndex = this.drivers[driverId].trips.length - 1
        }

        if (newIndex >= this.drivers[driverId].trips.length && this.drivers[driverId].options.page < Math.ceil(this.drivers[driverId].totalTrips / this.drivers[driverId].options.itemsPerPage)) {
          await this.$refs[`driver-${driverId}`].loadTrips({ page: this.drivers[driverId].options.page + 1 })
          newIndex = 0
        }

        if (newIndex > -1 && newIndex < this.drivers[driverId].trips.length) {
          const travel = this.drivers[driverId].trips[newIndex]
          await this.loadTrip({ travel: travel.id })
        }
      }

      // this.$refs[`driver-${entry.id}`].loadTrips({ includedTrip }) // TODO: figure it out if we need to update data
    },

    async fetchProfile (userId) {
      const response = await backend.fetchDriverProfile({ user_id: userId })

      this.profileData.profile = new UserProfile(response.data.profile)
      this.profileData.settings = new UserSettings(response.data.settings)
    },

    // load user profile and settings by userId
    async loadUserProfile (data = { userId: -1, username: '' }) {
      this.profileData = defaultDriverProfile(data.username, data.userId)

      if (!this.isAdmin) {
        return
      }

      if (!this.settingsDialog) {
        this.settingsDialog = true
      }

      this.profileData.isLoaded = false
      let isError = false

      try {
        await this.fetchProfile(data.userId)
      } catch (e) {
        isError = true
        this.processFailedRequest(e)
      } finally {
        this.profileData.isLoaded = !isError
        this.settingsDialog = !isError
      }
    },

    // load default settings
    async openDefaultSettings () {
      this.profileData = defaultDriverProfile(Vuetify.framework.lang.t('$vuetify.map.managerMap.defaultSettings'), -1)

      // check permissions and temporally disable for basic user work with settings
      if (!this.isSpecial) {
        return
      }

      if (!this.settingsDialog) {
        this.settingsDialog = true
      }

      this.profileData.isLoaded = false
      this.profileData.isDefaultSettings = true

      let isError = false

      try {
        const result = await backend.fetchDefaultSettings()
        this.profileData.settings = new UserSettings(result.data)
      } catch (e) {
        isError = true
        this.processFailedRequest(e)
      } finally {
        this.profileData.isLoaded = !isError
        this.settingsDialog = !isError
      }
    },

    // TODO: create template function
    // set 'isCameraCorrect' to true for all events
    async setAllCameraCorrectEvents () {
      try {
        // apply changes on the server
        await backend.setAllCameraCorrectTrip({ 'trip_id': this.travelData.travel.travelId })
        // apply changes on the client
        for (const event of this.travelData.travel.criticalEvents.values()) {
          event.isCameraCorrect = true
        }
      } catch (e) {
        this.processFailedRequest({ details: '$vuetify.errors.generalError' })
      }
    },

    // set 'isWearingMask' to true for all events
    async setAllWearingMaskEvents () {
      try {
        // apply changes on the server
        await backend.setAllMaskWearingTrip({ 'trip_id': this.travelData.travel.travelId })

        // apply changes on the client
        for (const event of this.travelData.travel.criticalEvents.values()) {
          event.isWearingMask = true
        }
      } catch (e) {
        this.processFailedRequest({ details: '$vuetify.errors.generalError' })
      }
    },

    // filter functions
    async filterColumn (data) {
      this.filters[data.column].value = data.value
      await this.getUsers() // update
    },
    async datetimeUpdate (data) {
      this.filters[data.column].value[data.type][data.mode] = data.value
      await this.getUsers() // update
    },
    async datetimeCleared (data) {
      this.filters[data.column].value = {
        start: defaultDatetimeFilterState(),
        finish: defaultDatetimeFilterState()
      }

      await this.getUsers() // update
    },

    async clearAll () {
      this.filters = defaultFilterState()
      const datetimeFilter = this.$refs.travel_datetime_filter[0]
      datetimeFilter.clearData(false) // datetimeCleared will not be called
      await this.getUsers() // update
    },

    specialOnly (column) {
      const filter = this.filters[column]
      if (!filter.hasOwnProperty('specialOnly')) {
        return false
      } else {
        return filter.specialOnly && !this.isSpecial
      }
    },

    async syncDriverTables () {
      const request = {
        includedDriver: this.travelData.travel.user.userId === 0 ? null : this.travelData.travel.user.userId,
        travelId: this.travelData.travel.user.userId === 0 ? null : this.travelData.travel.travelId
      }

      await this.getUsers(request)
    },
    async updateReportTripState () {
      await this.syncDriverTables()
    }
  },
  watch: {
    options: {
      async handler () {
        // we don't use mount for initial loading because first data loading occures here
        if (this.isLoaded) {
          await this.getUsers()
        }
      },
      deep: true
    },
    tripDialog (value) {
      if (!value) {
        this.travelData = defaultTravelData()
      }
    }
  },
  async created () {
    this['MANAGER_MAP_MODULE'] = MANAGER_MAP_MODULE
    this['distanceLocalized'] = distanceLocalized
    this['travelDateLocalized'] = travelDateLocalized
    this['valueFormatted'] = valueFormatted
    this['dateToCalendarEntry'] = dateToCalendarEntry

    try {
      await backend.getPapersList() // TODO: we need first empty call for the user restrictions gathering, make solution to acquire it before any calls (axios?)
    } catch (e) {

    } finally {
      this.isLoaded = true
    }
  },
  beforeRouteEnter (to, from, next) {
    next(async vm => {
      if (isCorrectQueryParams(to.query)) {
        await vm.loadTrip({ ...to.query, once: true })
      }

      await vm.syncDriverTables()
      // vm.isLoaded = true
    })
  },
  async beforeRouteUpdate (to, from, next) {
    if (isCorrectQueryParams(to.query)) {
      await this.loadTrip({ ...to.query, once: true })
    }

    await this.syncDriverTables()
    // this.isLoaded = true
    next()
  }
}
</script>

<style scoped>
.style-cursor {
  cursor: pointer;
}

.x-overflow {
  /*overflow-x: auto;*/
}

>>> .v-dialog {
  box-shadow: none;
}

>>> .v-data-table th {
  font-size: 14px;
}

.manager-datatable {
  height: 100%;
}

>>> .manager-datatable .v-data-table__progress [role='progressbar'] {
  position: absolute;
}

>>> .v-data-table__expanded.v-data-table__expanded__content tr:nth-child(even) {
  background: #f5f4db;
}

>>> .v-data-table__expanded.v-data-table__expanded__content .v-data-table th {
  font-size: 12px;
  max-width: 250px;
}

>>> .v-data-table tr:nth-child(even) {
  background: #e8f3ec;
}

/*>>> .cell-expanded thead tr:last-child th {*/
/*  border-bottom: 0;*/
/*}*/

/*>>> .cell-expanded tbody tr:not(:last-child) td:not(.v-data-table__mobile-row) {*/
/*  border-bottom: 0;*/
/*}*/

/*>>> .cell-expanded tbody tr:last-child td:not(.v-data-table__mobile-row) {*/
/*  border-bottom: 0;*/
/*}*/

/*>>> .cell-expanded td {*/
/*  !*padding: 0;*!*/
/*  height: 100%;*/
/*  min-height: 250px;*/
/*}*/

/*>>> .cell-expanded .v-date-picker-table--date th {*/
/*  text-align: center;*/
/*}*/

/*>>> .cell-expanded thead tr:hover:not(.v-data-table__expanded__content) {*/
/*  background: 0;*/
/*}*/

/*>>> .cell-expanded tbody tr:hover:not(.v-data-table__expanded__content) {*/
/*  background: 0;*/
/*}*/
</style>
